import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Field,
  Select,
  StudioState,
  TextArea,
  TextInput,
} from "@odingard/design-system";
import {
  SOP_CRITICALITIES,
  SOP_DATA_CLASSIFICATIONS,
  type SopCriticality,
  type SopDataClassification,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { sopApi } from "../../lib/api";
import { GovernedAction } from "../../governance/GovernedAction";

export function SopDashboard() {
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] = useState("");
  const [category, setCategory] = useState("");
  const [criticality, setCriticality] = useState<SopCriticality>("MODERATE");
  const [dataClassification, setDataClassification] = useState<SopDataClassification>("internal");
  const state = useAsync(async () => {
    const [procedures, runs] = await Promise.all([sopApi.procedures(orgId), sopApi.runs(orgId)]);
    return { procedures, runs };
  }, [orgId]);
  const actor = user?.displayName ?? user?.email ?? "Current actor";
  const drafted = title.trim().length > 0 && purpose.trim().length > 0 && category.trim().length > 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>SOP AI</h1>
          <p>Know how the work is done, and prove it was done that way.</p>
        </div>
        <Badge tone="info">Acting as {activeOrg?.name ?? "the current user"}</Badge>
      </div>
      {state.loading && <StudioState state="loading" description="Loading procedures and runs…" />}
      {state.error && (
        <StudioState
          state={state.error.message.includes("unreachable") ? "dependency_unavailable" : "error"}
          description={state.error.message}
          onRetry={state.reload}
        />
      )}
      {state.data && (
        <div className="ods-stack">
          <Card>
            <CardHeader
              title="Procedures"
              action={
                <GovernedAction
                  permission="procedures.author"
                  label="Draft a procedure"
                  title="Draft a procedure"
                  description={`Author: ${actor}. The procedure starts as a draft; nothing can be run until a version is published.`}
                  confirmLabel="Create procedure"
                  triggerVariant="primary"
                  onConfirm={() =>
                    sopApi
                      .createProcedure(orgId, {
                        title: title.trim(),
                        purpose: purpose.trim(),
                        category: category.trim(),
                        criticality,
                        ownerUserId: user?.id ?? "",
                        dataClassification,
                      })
                      .then((created) => {
                        setTitle("");
                        setPurpose("");
                        setCategory("");
                        return created;
                      })
                  }
                  onDone={state.reload}
                >
                  <Field label="Title">
                    {(id) => (
                      <TextInput
                        id={id}
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="Quarterly access review"
                      />
                    )}
                  </Field>
                  <Field label="Purpose" help="Why this procedure exists and what it protects.">
                    {(id) => (
                      <TextArea
                        id={id}
                        rows={3}
                        value={purpose}
                        onChange={(event) => setPurpose(event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Category">
                    {(id) => (
                      <TextInput
                        id={id}
                        value={category}
                        onChange={(event) => setCategory(event.target.value)}
                        placeholder="access"
                      />
                    )}
                  </Field>
                  <Field label="Criticality">
                    {(id) => (
                      <Select
                        id={id}
                        value={criticality}
                        onChange={(event) => setCriticality(event.target.value as SopCriticality)}
                      >
                        {SOP_CRITICALITIES.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field label="Data classification">
                    {(id) => (
                      <Select
                        id={id}
                        value={dataClassification}
                        onChange={(event) =>
                          setDataClassification(event.target.value as SopDataClassification)
                        }
                      >
                        {SOP_DATA_CLASSIFICATIONS.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  {!drafted && (
                    <p className="ods-denial">A title, a purpose and a category are required.</p>
                  )}
                </GovernedAction>
              }
            />
            <CardBody>
              {state.data.procedures.data.procedures.length === 0 ? (
                <StudioState
                  state="empty"
                  title="No procedures yet"
                  description="Draft the first procedure to describe how this work is done."
                />
              ) : (
                <DataTable
                  rows={state.data.procedures.data.procedures}
                  caption="SOP AI procedures"
                  columns={[
                    {
                      key: "title",
                      header: "Title",
                      render: (procedure) => (
                        <Link to={`/studio/sop-ai/procedures/${procedure.id}`}>{procedure.title}</Link>
                      ),
                    },
                    {
                      key: "state",
                      header: "State",
                      render: (procedure) => <Badge tone="neutral">{procedure.lifecycleState}</Badge>,
                    },
                    {
                      key: "criticality",
                      header: "SopCriticality",
                      render: (procedure) => procedure.criticality ?? "Unclassified",
                    },
                    {
                      key: "owner",
                      header: "Owner",
                      render: (procedure) => procedure.ownerName ?? procedure.ownerUserId ?? "Unassigned",
                    },
                  ]}
                />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Recent runs" />
            <CardBody>
              {state.data.runs.data.runs.length === 0 ? (
                <StudioState
                  state="empty"
                  title="No runs yet"
                  description="Start a run from a published procedure version."
                />
              ) : (
                <DataTable
                  rows={state.data.runs.data.runs}
                  caption="SOP AI runs"
                  columns={[
                    {
                      key: "run",
                      header: "Run",
                      render: (run) => (
                        <Link to={`/studio/sop-ai/runs/${run.id}`}>
                          {run.subjectReference ?? run.procedureId}
                        </Link>
                      ),
                    },
                    {
                      key: "state",
                      header: "State",
                      render: (run) => (
                        <Badge tone={run.state === "IN_PROGRESS" ? "warning" : "success"}>{run.state}</Badge>
                      ),
                    },
                    { key: "outcome", header: "Outcome", render: (run) => run.outcome ?? "Open" },
                    {
                      key: "performer",
                      header: "Performer",
                      render: (run) => run.performerId ?? "Unknown",
                    },
                  ]}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}
